import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, X, Building2, Truck } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { COMPANY_STATUSES, companyCreateSchema, type CompanyCreateInput } from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api, ApiError } from "@/lib/api";

interface Company {
  id: string;
  name: string;
  status: "active" | "prospect" | "past" | "lost";
  industry: string | null;
  size: string | null;
  isInternal: boolean;
  website: string | null;
  notes: string | null;
}

const filtersSchema = z.object({
  status: z.enum(["all", ...COMPANY_STATUSES]).default("all"),
  q: z.string().default(""),
});

type Filters = z.input<typeof filtersSchema>;

const STATUS_META: Record<Company["status"], { label: string; badge: string; ring: string }> = {
  active: { label: "Activo", badge: "badge-success", ring: "bg-success" },
  prospect: { label: "Prospecto", badge: "badge-info", ring: "bg-info" },
  past: { label: "Pasado", badge: "badge-ghost", ring: "bg-base-300" },
  lost: { label: "Perdido", badge: "badge-error", ring: "bg-error" },
};

export const Route = createFileRoute("/crm")({
  component: CrmPage,
  validateSearch: filtersSchema,
});

function CrmPage() {
  const filters = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [createForbidden, setCreateForbidden] = useState(false);
  // null = cerrado; { kind: "create" } = nuevo; { kind: "edit", company } = editar.
  const [dialog, setDialog] = useState<
    { kind: "create" } | { kind: "edit"; company: Company } | null
  >(null);

  // CRM = clientes y prospectos. Excluye proveedores (gestionados desde
  // Finanzas → Proveedores). Si una empresa es ambos, hay que des-marcar
  // isSupplier o crear un endpoint multi-rol más adelante.
  const { data, isLoading } = useQuery<{ companies: Company[] }>({
    queryKey: ["companies", "crm"],
    queryFn: () => api.get("/companies?isSupplier=0"),
  });

  const qc = useQueryClient();
  const markSupplier = useMutation({
    mutationFn: (id: string) => api.patch(`/companies/${id}`, { isSupplier: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
    },
  });

  const setFilter = (key: keyof Filters, value: string) => {
    navigate({
      to: ".",
      search: (prev: Filters) => ({ ...prev, [key]: value }),
      replace: true,
    });
  };

  const clearFilters = () =>
    navigate({ to: ".", search: { status: "all", q: "" } as Filters, replace: true });

  const filtered = (data?.companies ?? []).filter((c) => {
    if (filters.status !== "all" && c.status !== filters.status) return false;
    if (filters.q.trim()) {
      const q = filters.q.trim().toLowerCase();
      if (!c.name.toLowerCase().includes(q) && !(c.industry ?? "").toLowerCase().includes(q)) {
        return false;
      }
    }
    return true;
  });

  const anyFilter = filters.status !== "all" || !!filters.q;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="crm"
        title="Clientes y prospectos"
        subtitle="Empresas con las que tenemos relación comercial — activas, prospectos, pasadas o perdidas."
        actions={
          !createForbidden && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setDialog({ kind: "create" })}
            >
              <Plus className="size-4" /> Nueva empresa
            </button>
          )
        }
      />

      <div className="card-elevated p-3 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:flex-1 sm:min-w-48">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
          <input
            value={filters.q}
            onChange={(e) => setFilter("q", e.target.value)}
            placeholder="Buscar por nombre o industria"
            className="input input-bordered input-sm w-full pl-9"
          />
        </div>
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(v) => setFilter("status", v)}
          options={[
            { value: "all", label: "Todos" },
            { value: "active", label: "Activos" },
            { value: "prospect", label: "Prospectos" },
            { value: "past", label: "Pasados" },
            { value: "lost", label: "Perdidos" },
          ]}
        />
        {anyFilter && (
          <button type="button" onClick={clearFilters} className="btn btn-ghost btn-sm gap-1.5">
            <X className="size-3.5" /> Limpiar
          </button>
        )}
        <div className="text-sm text-base-content/55">
          {data ? `${filtered.length} de ${data.companies.length}` : null}
        </div>
      </div>

      {isLoading && (
        <div className="grid place-items-center py-20">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map((c) => {
          const meta = STATUS_META[c.status];
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setDialog({ kind: "edit", company: c })}
              className="card-elevated p-5 group flex flex-col gap-3 hover:[&]:card-elevated-hover text-left"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      aria-hidden
                      className={`size-2 rounded-full ring-4 ${meta.ring}/30 ${meta.ring} shrink-0`}
                    />
                    <span className="text-xs font-semibold uppercase tracking-wider text-base-content/60">
                      {c.industry ?? "—"}
                    </span>
                  </div>
                  <h3 className="font-display font-semibold text-base-content text-lg leading-snug">
                    {c.name}
                  </h3>
                </div>
                <div className="flex items-center gap-1 shrink-0 mt-0.5">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (window.confirm(`Mover "${c.name}" a Proveedores?`)) {
                        markSupplier.mutate(c.id);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        if (window.confirm(`Mover "${c.name}" a Proveedores?`)) {
                          markSupplier.mutate(c.id);
                        }
                      }
                    }}
                    aria-disabled={markSupplier.isPending}
                    className="btn btn-ghost btn-xs btn-square text-base-content/40 hover:text-warning"
                    title="Mover a Proveedores (es un proveedor, no un cliente)"
                    aria-label="Mover a Proveedores"
                  >
                    <Truck className="size-3.5" />
                  </span>
                  <Building2 className="size-4 text-base-content/30 group-hover:text-primary transition-colors" />
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-base-300/50">
                <span className={`badge badge-sm ${meta.badge}`}>{meta.label}</span>
                {c.size && <span className="text-xs text-base-content/55">{c.size}</span>}
              </div>
            </button>
          );
        })}
        {data && data.companies.length === 0 && (
          <div className="col-span-full">
            <div className="card-elevated py-16 text-center">
              <div className="size-14 rounded-full bg-primary/10 grid place-items-center mx-auto mb-4">
                <Building2 className="size-6 text-primary" />
              </div>
              <h3 className="font-display font-semibold text-base-content mb-1">
                Aún no hay empresas
              </h3>
              <p className="text-sm text-base-content/60 mb-5">
                Agrega prospectos o clientes para empezar a trackearlos aquí.
              </p>
              {!createForbidden && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setDialog({ kind: "create" })}
                >
                  Crear el primero
                </button>
              )}
            </div>
          </div>
        )}
        {data && filtered.length === 0 && data.companies.length > 0 && (
          <div className="col-span-full">
            <div className="card-elevated py-12 text-center">
              <p className="text-sm text-base-content/60 mb-3">
                Ninguna empresa coincide con los filtros.
              </p>
              <button type="button" onClick={clearFilters} className="btn btn-ghost btn-sm">
                Limpiar filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {dialog && (
        <CompanyDialog
          key={dialog.kind === "edit" ? dialog.company.id : "new"}
          company={dialog.kind === "edit" ? dialog.company : null}
          onClose={() => setDialog(null)}
          onForbidden={() => {
            setCreateForbidden(true);
            setDialog(null);
          }}
        />
      )}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-xs uppercase tracking-wider text-base-content/55 hidden sm:inline">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="select select-bordered select-sm"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// ─── Company dialog (crea o edita) ────────────────────────────────────────

function CompanyDialog({
  company,
  onClose,
  onForbidden,
}: {
  company: Company | null;
  onClose: () => void;
  onForbidden: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = company !== null;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CompanyCreateInput>({
    resolver: zodResolver(companyCreateSchema),
    defaultValues: company
      ? {
          name: company.name,
          status: company.status,
          industry: company.industry ?? undefined,
          size: company.size ?? undefined,
          website: company.website ?? undefined,
          notes: company.notes ?? undefined,
        }
      : { status: "prospect" },
  });

  const save = useMutation({
    mutationFn: (input: CompanyCreateInput) =>
      isEdit
        ? api.patch(`/companies/${company.id}`, input)
        : api.post<{ id: string }>("/companies", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 403) onForbidden();
    },
  });

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box card-elevated">
        <h3 className="text-lg font-display font-semibold mb-4">
          {isEdit ? `Editar ${company.name}` : "Nueva empresa"}
        </h3>
        <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-3">
          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">Nombre</span>
            </label>
            <input
              className="input input-bordered w-full"
              placeholder="Ej. Banco Azteca"
              autoFocus
              {...register("name")}
            />
            {errors.name && (
              <span className="text-xs text-error mt-1 block">{errors.name.message}</span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Status</span>
              </label>
              <select className="select select-bordered w-full" {...register("status")}>
                <option value="prospect">Prospecto</option>
                <option value="active">Activo</option>
                <option value="past">Pasado</option>
                <option value="lost">Perdido</option>
              </select>
            </div>
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Industria</span>
              </label>
              <input
                className="input input-bordered w-full"
                placeholder="Fintech, Retail, etc."
                {...register("industry")}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Tamaño</span>
              </label>
              <input
                className="input input-bordered w-full"
                placeholder="50 empleados, etc."
                {...register("size")}
              />
            </div>
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Sitio web</span>
              </label>
              <input
                type="url"
                className="input input-bordered w-full"
                placeholder="https://..."
                {...register("website")}
              />
              {errors.website && (
                <span className="text-xs text-error mt-1 block">{errors.website.message}</span>
              )}
            </div>
          </div>

          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">Notas</span>
            </label>
            <textarea
              rows={3}
              className="textarea textarea-bordered w-full"
              placeholder="Cómo llegamos a ellos, contactos clave, etc."
              {...register("notes")}
            />
          </div>

          {save.isError && !(save.error instanceof ApiError && save.error.status === 403) && (
            <div className="alert alert-error text-sm">
              {save.error instanceof Error ? save.error.message : "Error al guardar"}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-3 border-t border-base-300/60">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={save.isPending}>
              {save.isPending && <span className="loading loading-spinner loading-xs" />}
              {isEdit ? "Guardar" : "Crear empresa"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
